import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError } from '../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header';
import ViewKeysModule from './ViewKeysModule';

/** DTOs */
interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}
interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface KeyDTO {
  id: string;
  keyCode: string;
  doorId: string;
  roomId: string;
  vehicleId: string | null;
  noOfKeys: number;
  separatedKeys: number | 0;
  bundles: number | 0;
  responsibleParty: string | null;
  isRestricted: boolean;
  handoverStatus: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}
interface DoorDTO {
  id: number;
  name: string;
  roomId: number;
}
interface GetLocationsResponse {
  locations: LocationDTO[];
  total: number;
}
interface GetRoomsResponse {
  rooms: RoomDTO[];
  total: number;
  page: number;
  pageSize: number;
  filtersApplied?: string[];
}
interface GetKeysResponse {
  data: KeyDTO[];
}
interface GetDoorsResponse {
  doors: DoorDTO[];
  total: number;
}

/** Status → classes */
const roomStatusClasses: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-25 border-success',
  INACTIVE: 'bg-warning bg-opacity-25 border-warning',
  ONGOING_REPAIR: 'bg-danger bg-opacity-25 border-danger',
  OCCUPIED: 'border-info bg-info-subtle',
  RESERVED: 'border-primary bg-primary-subtle',
};
const getStatusClass = (status?: string) =>
  roomStatusClasses[status?.toUpperCase() ?? ''] || 'border-light bg-light';

const navItems = [
  { label: 'Key Allocations', icon: 'ti ti-settings', link: all_routes.keyAllocations, permission: 'ROLE_GET_KEY_ALLOCATIONS' },
  { label: 'Add Keys', icon: 'ti ti-settings', link: all_routes.keys, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Vehicle Keys', icon: 'ti ti-car', link: all_routes.vehiclekey, permission: 'ROLE_ADD_KEY' },
];

/** Name with numeric-first sort */
const numericFirstSort = (a: string, b: string) => {
  const na = parseInt(a.replace(/\D+/g, ''), 10);
  const nb = parseInt(b.replace(/\D+/g, ''), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const Keys: React.FC = () => {
  const locationHook = useLocation();
  const navigate = useNavigate();

  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [rooms, setRooms] = useState<RoomDTO[]>([]);
  const [keyCounts, setKeyCounts] = useState<Record<number, number>>({});
  const [doorCounts, setDoorCounts] = useState<Record<number, number>>({});

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorIds, setSelectedFloorIds] = useState<number[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedRoom, setSelectedRoom] = useState<RoomDTO | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    return token;
  };

  /** Initial: locations */
  useEffect(() => {
    const run = async () => {
      if (!checkToken()) return;
      if (!checkPermission('ROLE_GET_LOCATIONS')) {
        showError('You do not have permission to view locations.');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const payload = { filterType: '', filterValue: '', date: '' };
        const res: AxiosResponse = await apiDefinitions.getLocations('1', payload);
        if (res.status === 200) {
          const data: GetLocationsResponse = res.data.data;
          setLocations(data.locations || []);
          if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.');
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch locations');
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else showError('Failed to fetch locations. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, []);

  /** When location changes → buildings */
  useEffect(() => {
    const run = async () => {
      if (!selectedLocationId) {
        setBuildings([]);
        setSelectedBuildingId(null);
        setFloors([]);
        setRooms([]);
        setSelectedFloorIds([]);
        setKeyCounts({});
        setDoorCounts({});
        return;
      }
      if (!checkToken()) return;
      if (!checkPermission('ROLE_GET_LOCATIONS')) {
        showError('You do not have permission to view buildings.');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
        if (res.status === 200) {
          const data: BuildingDTO[] = res.data.data;
          setBuildings(data || []);
          setSelectedBuildingId(null);
          setFloors([]);
          setRooms([]);
          setSelectedFloorIds([]);
          setKeyCounts({});
          setDoorCounts({});
          if ((data || []).length === 0) setError(`No buildings found for ${selectedLocationName}.`);
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch buildings');
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else showError('Failed to fetch buildings. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [selectedLocationId]);

  /** When building changes → fetch floors + rooms + keys + doors */
  useEffect(() => {
    const run = async () => {
      if (!selectedBuildingId) {
        setFloors([]);
        setRooms([]);
        setSelectedFloorIds([]);
        setKeyCounts({});
        setDoorCounts({});
        return;
      }
      if (!checkToken()) return;

      setIsLoading(true);
      setError(null);
      try {
        if (!checkPermission('ROLE_VIEW_FLOOR') || !checkPermission('ROLE_GET_ROOMS')) {
          showError('You do not have permission to view floors/rooms.');
          setIsLoading(false);
          return;
        }
        if (!checkPermission('ROLE_GET_KEYS')) {
          showError('You do not have permission to view keys.');
          setKeyCounts({});
        }

        const [floorsRes, roomsRes] = await Promise.all([
          apiDefinitions.getFloorsByBuildingId(selectedBuildingId),
          apiDefinitions.getRooms('1', { filterType: 'buildingId', filterValue: String(selectedBuildingId), date: '' }),
        ]);

        // Floors
        if (floorsRes.status === 200) {
          const fData: FloorDTO[] = floorsRes.data.data || [];
          const sortedFloors = fData.sort((a, b) => {
            if (a.floorNumber === 0) return -1;
            if (b.floorNumber === 0) return 1;
            return a.floorNumber - b.floorNumber;
          });
          setFloors(sortedFloors);
          setSelectedFloorIds(sortedFloors.map(f => f.id));
          if (sortedFloors.length === 0) setError(`No floors found for building ID ${selectedBuildingId}.`);
        } else {
          showError(floorsRes.data?.statusDescription || 'Failed to fetch floors');
        }

        // Rooms, Keys, and Doors
        if (roomsRes.status === 200) {
          const rData: GetRoomsResponse = roomsRes.data.data;
          const sortedRooms = (rData.rooms || []).slice().sort((a, b) => numericFirstSort(a.name, b.name));
          setRooms(sortedRooms);
          if ((rData.rooms || []).length === 0) setError(`No rooms found for building ID ${selectedBuildingId}.`);
          else {
            // Fetch keys for each room
            const keyPromises = sortedRooms.map(room =>
              apiDefinitions.getKeysByRoomId(room.id)
                .then(res => {
                  if (res.status === 200) {
                    const keys: KeyDTO[] = res.data.data || [];
                    const totalKeys = keys.reduce((sum, key) => sum + key.noOfKeys, 0);
                    return { roomId: room.id, total: totalKeys };
                  }
                  return { roomId: room.id, total: 0 };
                })
                .catch(e => {
                  return { roomId: room.id, total: 0 };
                })
            );
            const keyResults = await Promise.all(keyPromises);
            const keyCountMap = keyResults.reduce((acc, { roomId, total }) => ({ ...acc, [roomId]: total }), {});
            setKeyCounts(keyCountMap);

            // Fetch doors for each room using getDoorsByRoomId
            const doorPromises = sortedRooms.map(room =>
              apiDefinitions.getDoorsByRoomId(room.id)
                .then(res => {
                  return {
                    roomId: room.id,
                    total: res.status === 200 ? (res.data.data as DoorDTO[]).length : 0,
                  };
                })
                .catch(e => {
                  return { roomId: room.id, total: 0 };
                })
            );
            const doorResults = await Promise.all(doorPromises);
            const doorCountMap = doorResults.reduce((acc, { roomId, total }) => ({ ...acc, [roomId]: total }), {});
            setDoorCounts(doorCountMap);
          }
        } else {
          showError(roomsRes.data?.statusDescription || 'Failed to fetch rooms');
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else showError('Failed to fetch floors/rooms. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [selectedBuildingId]);

  const selectedLocationName = useMemo(
    () => (selectedLocationId ? (locations.find(l => l.id === selectedLocationId)?.name || '') : ''),
    [selectedLocationId, locations]
  );
  const selectedBuildingName = useMemo(
    () => (selectedBuildingId ? (buildings.find(b => b.id === selectedBuildingId)?.name || '') : ''),
    [selectedBuildingId, buildings]
  );

  const currentDateTime = new Date().toLocaleString('en-US', {
    timeZone: 'Asia/Kolkata',
    hour12: true,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const openRoom = async (roomId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_ROOMS')) {
      showError('You do not have permission to view room details.');
      return;
    }
    setIsModalLoading(true);
    setSelectedRoom(null);
    try {
      const res: AxiosResponse = await apiDefinitions.getRoomById(roomId);
      if (res.status === 200 && res.data?.data?.id) setSelectedRoom(res.data.data as RoomDTO);
      else showError(res.data?.statusDescription || 'Failed to fetch room details');
    } catch (e: any) {
      showError('Failed to fetch room details. Please try again.');
    } finally {
      setIsModalLoading(false);
      setShowModal(true);
    }
  };

  /** UI helpers */
  const RoomCard: React.FC<{ room: RoomDTO }> = ({ room }) => (
    <div
      className={`card h-100 cursor-pointer border border-2 room-card ${getStatusClass(room.status)}`}
      onClick={() => openRoom(room.id)}
      title={room.name}
    >
      <div className="card-body py-1 px-2 d-flex justify-content-between align-items-center">
        <span className="fw-semibold text-dark room-card__label">{room.name}</span>
        <div className="d-flex gap-1">
          <span className="badge bg-dark rounded-pill" title={`${keyCounts[room.id] || 0} key(s)`}>
            <i className="ti ti-key me-1"></i>{keyCounts[room.id] || 0}
          </span>
          <span className="badge bg-secondary rounded-pill" title={`${doorCounts[room.id] || 0} door(s)`}>
            <i className="ti ti-door me-1"></i>{doorCounts[room.id] || 0}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      {/* Tiny CSS for compact cards */}
      <style>{`
        .room-card {
          min-height: 48px;           /* smaller card height */
          border-radius: .5rem;
        }
        .room-card .card-body {
          line-height: 1;             /* tighter line height */
        }
        .room-card__label {
          font-size: 0.9rem;          /* smaller text, still readable */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 60%;           /* leave space for key and door badges */
          display: inline-block;
        }
        .room-card .badge {
          font-size: 0.8rem;        /* smaller badge */
          padding: 0.2rem 0.5rem;
        }
        @media (min-width: 768px) {
          .room-card { min-height: 44px; } /* a touch smaller on md+ */
          .room-card__label { font-size: 0.85rem; }
          .room-card .badge { font-size: 0.75rem; }
        }
      `}</style>

      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Key Management/Key Dashboard/Add Key</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, i) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={i}>
                        <Link to={item.link} className={`nav-link px-0 ${locationHook.pathname === item.link ? 'active' : ''}`}>
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                {/* selectors */}
                <div className="row g-2 mb-4">
                  <div className="col-md-4">
                    <label className="form-label">Select Location</label>
                    <select
                      className="form-select"
                      value={selectedLocationId || ''}
                      onChange={(e) => setSelectedLocationId(e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoading}
                    >
                      <option value="">{locations.length === 0 ? 'No locations available' : 'Select a location'}</option>
                      {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Select Building</label>
                    <select
                      className="form-select"
                      value={selectedBuildingId || ''}
                      onChange={(e) => setSelectedBuildingId(e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoading || !selectedLocationId}
                    >
                      <option value="">{buildings.length === 0 ? 'No buildings available' : 'Select a building'}</option>
                      {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Select Floors</label>
                    <div className="d-flex flex-wrap gap-2">
                      {floors.map(f => (
                        <div key={f.id} className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`floor-${f.id}`}
                            checked={selectedFloorIds.includes(f.id)}
                            onChange={() =>
                              setSelectedFloorIds(prev => prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id])
                            }
                            disabled={isLoading || !selectedBuildingId}
                          />
                          <label className="form-check-label" htmlFor={`floor-${f.id}`}>{f.name}</label>
                        </div>
                      ))}
                      {floors.length === 0 && <span>No floors available</span>}
                    </div>
                  </div>
                </div>

                {isLoading && (
                  <div className="text-center">
                    <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                  </div>
                )}

                {error && <div className="alert alert-danger" role="alert">{error}</div>}

                {selectedLocationId && selectedBuildingId && floors.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-center mb-4">
                      {selectedLocationName} - 🏢 {selectedBuildingName}
                    </h3>

                    {/* FLOOR STRIPS */}
                    <div className="d-flex flex-column">
                      {floors.map(floor =>
                        selectedFloorIds.includes(floor.id) ? (
                          <div key={floor.id} className="floor-strip card p-3">
                            <div className="d-flex align-items-stretch">
                              {/* Vertical floor badge */}
                              <div className="me-3 d-none d-md-flex align-items-center">
                                <span
                                  className="badge bg-primary rounded-3"
                                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '1px' }}
                                >
                                  {floor.name}
                                </span>
                              </div>

                              <div className="container-fluid">
                                {(() => {
                                  const fr = rooms
                                    .filter(r => r.floorId === floor.id)
                                    .sort((a, b) => numericFirstSort(a.name, b.name));
                                  const top = fr.slice(0, 6);
                                  const mid = fr.slice(6, 12);
                                  const mid1 = fr.slice(12, 18);
                                  const bottom = fr.slice(18, 24);

                                  return (
                                    <>
                                      {/* Top row */}
                                      <div className="row g-2 mb-2 align-items-stretch">
                                        {top.map(r => (
                                          <div key={r.id} className="col-6 col-sm-3 col-md-2">
                                            <RoomCard room={r} />
                                          </div>
                                        ))}
                                      </div>
                                      {/* Mid row */}
                                      <div className="row g-2 align-items-stretch">
                                        {mid.map(r => (
                                          <div key={r.id} className="col-6 col-sm-3 col-md-2">
                                            <RoomCard room={r} />
                                          </div>
                                        ))}
                                      </div>
                                      {/* Mid row */}
                                      <div className="row g-2 align-items-stretch">
                                        {mid1.map(r => (
                                          <div key={r.id} className="col-6 col-sm-3 col-md-2">
                                            <RoomCard room={r} />
                                          </div>
                                        ))}
                                      </div>
                                      {/* Bottom row */}
                                      <div className="row g-2 align-items-stretch">
                                        {bottom.map(r => (
                                          <div key={r.id} className="col-6 col-sm-3 col-md-2">
                                            <RoomCard room={r} />
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <ViewKeysModule
              show={showModal}
              onHide={() => {
                setShowModal(false);
                setSelectedRoom(null);
              }}
              room={isModalLoading ? null : selectedRoom}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Keys;